import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getProjectSummary, updateProjectStep8, createCheckoutSession, devMarkProjectPaid, downloadProjectPDF, downloadDCWMPPDF, downloadCMPPDF, getPricing, checkFreePlanEligible, redeemFreePlan, getCurrentUser } from '../../services/api';

// Map measure IDs to display names
const MEASURE_NAMES = {
  signage: 'Bin Signage',
  education: 'Tenant Education',
  accessibility: 'Accessible Bin Storage',
  ventilation: 'Ventilated Storage',
  drainage: 'Wash-down Facilities',
  lighting: 'Adequate Lighting',
  collection_access: 'Collection Vehicle Access',
  hardstand: 'Hardstand Surface',
  containment: 'Spill Containment',
  pest_control: 'Pest Management',
  bulky_waste: 'Bulky Waste Arrangements',
  review: 'Regular Review'
};

// Helper function to format bin configuration display
const formatBinDisplay = (streamData) => {
  if (!streamData) return '0x 0L';
  
  // Check if this is a custom configuration with bin_breakdown
  if (streamData.bin_breakdown && streamData.bin_breakdown.length > 0) {
    return streamData.bin_breakdown
      .filter(b => b.count > 0)
      .map(b => `${b.count}x ${b.size}L`)
      .join(' + ') || '0x 0L';
  }
  
  // Standard configuration with single bin_size
  const bins = streamData.bins_needed || 0;
  const size = streamData.bin_size || 0;
  return `${bins}x ${size}L`;
};

// Helper function to format numbers with max 1 decimal place
const formatNumber = (value) => {
  if (value === null || value === undefined) return '0';
  const num = parseFloat(value);
  if (isNaN(num)) return '0';
  // Round to 1 decimal place and remove trailing zeros
  return num.toFixed(1).replace(/\.0$/, '');
};

// Default pricing constants (will be overridden by dynamic pricing)
const DEFAULT_BASE_PRICE = 450.00;
const GST_RATE = 0.10;

const Step8ReviewPayment = ({ project, onComplete, onBack, selectedAddons = [], isDCWMP = false, isCMP = false }) => {
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const [documentDate, setDocumentDate] = useState('');
  const [pricingData, setPricingData] = useState(null);
  const [addonsConfig, setAddonsConfig] = useState({});
  const [currentUserEmail, setCurrentUserEmail] = useState('');
  // ==================== FREE PLAN PROMO (START) ====================
  const [isFreePlanEligible, setIsFreePlanEligible] = useState(false);
  const [freePlanIneligibleReason, setFreePlanIneligibleReason] = useState(null);
  const [redeemingFreePlan, setRedeemingFreePlan] = useState(false);
  // ==================== FREE PLAN PROMO (END) ====================
  
  // Payment bypass allowed user (for testing purposes)
  const BYPASS_ALLOWED_EMAIL = 'nathan.smith.connectsus@gmail.com';

  // Calculate dynamic base price from pricing data
  const getBasePrice = () => {
    if (!pricingData) return DEFAULT_BASE_PRICE;
    const planKey = project?.plan_type === 'demolition_construction_wmp' ? 'dcwmp' : 
                    project?.plan_type === 'bundled_wmp' ? 'bundled' : 
                    project?.plan_type === 'construction_management_plan' ? 'cmp' : 'opwmp';
    const devType = project?.development_type || 'Residential';
    return pricingData[planKey]?.[devType] || DEFAULT_BASE_PRICE;
  };

  // Calculate add-ons total
  const addonsTotal = selectedAddons.reduce((total, key) => {
    return total + (addonsConfig[key]?.price || 0);
  }, 0);

  const basePrice = getBasePrice();
  const subtotal = basePrice + addonsTotal;
  const gst = subtotal * GST_RATE;
  const total = subtotal + gst;

  useEffect(() => {
    loadSummary();
    loadPricing();
    loadCurrentUser();
    // ==================== FREE PLAN PROMO (START) ====================
    checkFreePlanEligibility();
    // ==================== FREE PLAN PROMO (END) ====================
  }, [project?.id]);

  // ==================== FREE PLAN PROMO (START) ====================
  const checkFreePlanEligibility = async () => {
    try {
      // Pass project ID to check plan type eligibility (bundled plans not allowed)
      const result = await checkFreePlanEligible(project?.id);
      setIsFreePlanEligible(result.eligible);
      setFreePlanIneligibleReason(result.reason || null);
    } catch (err) {
      console.error('Failed to check free plan eligibility:', err);
      setIsFreePlanEligible(false);
      setFreePlanIneligibleReason(null);
    }
  };

  const handleRedeemFreePlan = async () => {
    if (!project?.id || redeemingFreePlan) return;
    
    setRedeemingFreePlan(true);
    setError('');
    
    try {
      await redeemFreePlan(project.id);
      // Reload the page to show the completed state
      window.location.reload();
    } catch (err) {
      console.error('Failed to redeem free plan:', err);
      setError(err.response?.data?.detail || 'Failed to redeem free plan');
      setRedeemingFreePlan(false);
    }
  };
  // ==================== FREE PLAN PROMO (END) ====================

  const loadPricing = async () => {
    try {
      const pricing = await getPricing();
      setPricingData(pricing);
      setAddonsConfig(pricing.addons || {});
    } catch (err) {
      console.error('Failed to load pricing:', err);
    }
  };

  // Load current user email for bypass check
  const loadCurrentUser = async () => {
    try {
      const user = await getCurrentUser();
      setCurrentUserEmail(user?.email || '');
    } catch (err) {
      console.error('Failed to load current user:', err);
    }
  };

  const loadSummary = async () => {
    console.log('loadSummary called, project:', project);
    
    if (!project?.id) {
      console.log('No project ID, setting error');
      setError('No project ID provided');
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      setError('');
      console.log('Fetching summary for project:', project.id);
      const data = await getProjectSummary(project.id);
      console.log('Summary data received:', data);
      
      if (!data || !data.project) {
        throw new Error('Invalid response from server');
      }
      
      setSummary(data);
      
      // Set default document date to today if not already set
      if (data.project.document_date) {
        setDocumentDate(data.project.document_date.split('T')[0]);
      } else {
        const today = new Date().toISOString().split('T')[0];
        setDocumentDate(today);
      }
    } catch (err) {
      console.error('Failed to load summary:', err);
      setError(err.response?.data?.detail || err.message || 'Failed to load project summary. Please try again.');
    } finally {
      console.log('loadSummary finished, setting loading to false');
      setLoading(false);
    }
  };

  const handleDateChange = async (e) => {
    const newDate = e.target.value;
    setDocumentDate(newDate);
    
    try {
      await updateProjectStep8(project.id, { document_date: newDate });
    } catch (err) {
      console.error('Failed to save date:', err);
    }
  };

  const handlePayment = async () => {
    try {
      setProcessing(true);
      setError('');
      
      // Save the document date and selected add-ons
      await updateProjectStep8(project.id, { 
        document_date: documentDate,
        selected_addons: selectedAddons 
      });
      
      // Create checkout session with selected add-ons
      const { checkout_url } = await createCheckoutSession(project.id, selectedAddons);
      
      // Redirect to Stripe Checkout
      window.location.href = checkout_url;
    } catch (err) {
      console.error('Payment error:', err);
      const errorMessage = err.response?.data?.detail || 'Failed to initiate payment.';
      if (err.response?.status === 401) {
        setError('Session expired. Please log in again to continue.');
      } else {
        setError(errorMessage + ' Please try again or contact support at support@wasteplanning.com.au');
      }
      setProcessing(false);
    }
  };

  // Handle PDF download for paid projects
  const handleDownloadPDF = async () => {
    try {
      setProcessing(true);
      setError('');
      
      // Check if user is authenticated
      const token = localStorage.getItem('opwmp_token');
      if (!token) {
        setError('Session expired. Please log out and log back in.');
        setProcessing(false);
        return;
      }
      
      console.log('Downloading PDF for paid project...');
      // Use correct PDF download endpoint based on plan type
      const blob = isCMP 
        ? await downloadCMPPDF(project.id)
        : isDCWMP 
          ? await downloadDCWMPPDF(project.id) 
          : await downloadProjectPDF(project.id);
      
      console.log('Creating download link...');
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const prefix = isCMP ? 'CMP' : isDCWMP ? 'DCWMP' : 'OpWMP';
      link.setAttribute('download', `${prefix}_${project.project_name?.replace(/\s+/g, '_') || project.id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
    } catch (err) {
      console.error('PDF download error:', err);
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to download PDF.';
      const statusCode = err.response?.status;
      
      if (statusCode === 401) {
        setError('Session expired. Please log out and log back in, then try again.');
      } else if (statusCode === 404) {
        setError('Project not found. Please refresh the page and try again.');
      } else if (statusCode === 500 || errorMessage.toLowerCase().includes('pdf') || errorMessage.toLowerCase().includes('generate')) {
        setError(
          'We encountered an issue downloading your PDF. This is usually temporary. ' +
          'Please try again in a few moments. If the problem persists, contact support at support@wasteplanning.com.au'
        );
      } else {
        setError(errorMessage + ' If this issue persists, please contact support at support@wasteplanning.com.au');
      }
    } finally {
      setProcessing(false);
    }
  };

  // Dev test bypass for PDF generation without payment
  const handleDevTest = async () => {
    try {
      setProcessing(true);
      setError('');
      
      // Check if user is authenticated
      const token = localStorage.getItem('opwmp_token');
      if (!token) {
        setError('Session expired. Please log out and log back in.');
        setProcessing(false);
        return;
      }
      
      console.log('Step 1: Updating project step8...');
      await updateProjectStep8(project.id, { 
        document_date: documentDate,
        selected_addons: selectedAddons 
      });
      
      console.log('Step 2: Marking project as paid...');
      await devMarkProjectPaid(project.id);
      
      console.log('Step 3: Downloading PDF...');
      // Use correct PDF download endpoint based on plan type
      const blob = isCMP 
        ? await downloadCMPPDF(project.id)
        : isDCWMP 
          ? await downloadDCWMPPDF(project.id) 
          : await downloadProjectPDF(project.id);
      
      console.log('Step 4: Creating download link...');
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const prefix = isCMP ? 'CMP' : isDCWMP ? 'DCWMP' : 'OpWMP';
      link.setAttribute('download', `${prefix}_${project.project_name?.replace(/\s+/g, '_') || project.id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      navigate('/dashboard');
    } catch (err) {
      console.error('Dev test error:', err);
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to generate PDF.';
      const statusCode = err.response?.status;
      
      if (statusCode === 401) {
        setError('Session expired. Please log out and log back in, then try again.');
      } else if (statusCode === 404) {
        setError('Project not found. Please refresh the page and try again.');
      } else if (statusCode === 500 || errorMessage.toLowerCase().includes('pdf') || errorMessage.toLowerCase().includes('generate')) {
        setError(
          'We encountered an issue generating your PDF. This is usually temporary. ' +
          'Please try again in a few moments. If the problem persists, contact support at support@wasteplanning.com.au'
        );
      } else {
        setError(errorMessage + ' If this issue persists, please contact support at support@wasteplanning.com.au');
      }
    } finally {
      setProcessing(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-AU', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const formatDocumentDate = (dateString) => {
    if (!dateString) return 'Not set';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-AU', { 
      year: 'numeric', 
      month: 'long'
    });
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-8 text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
        <p className="mt-4 text-gray-600">Loading project summary...</p>
        <button
          onClick={() => {
            setLoading(false);
            setError('Loading timed out. Please try again.');
          }}
          className="mt-4 text-sm text-blue-600 hover:text-blue-800 underline"
        >
          Taking too long? Click here
        </button>
      </div>
    );
  }

  if (!summary) {
    return (
      <div className="bg-white rounded-lg shadow p-8 text-center">
        <div className="text-red-600 mb-4">
          <svg className="mx-auto h-12 w-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <h2 className="text-xl font-bold text-gray-900 mb-2">Unable to Load Summary</h2>
        <p className="text-gray-600 mb-4">{error || 'An unexpected error occurred'}</p>
        <div className="flex justify-center gap-4">
          <button
            onClick={loadSummary}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Retry
          </button>
          <button
            onClick={onBack}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow">
      {/* Header with Back button */}
      <div className="px-8 py-6 border-b border-gray-200">
        <div className="flex items-center justify-between mb-4">
          <button
            type="button"
            onClick={onBack}
            disabled={processing}
            className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
          >
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back
          </button>
        </div>
        <h2 className="text-2xl font-bold text-gray-900">{(isDCWMP || isCMP) ? 'Step 7' : 'Step 8'}: Review & Payment</h2>
        <p className="mt-1 text-sm text-gray-600">
          Review your project details and complete payment to generate your {isCMP ? 'Construction Management' : isDCWMP ? 'Demolition & Construction' : 'Operational'} Waste Management Plan.
        </p>
      </div>

      <div className="px-8 py-6 space-y-8">
        {/* Error Display */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-md p-4">
            <div className="flex">
              <svg className="h-5 w-5 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              <p className="ml-3 text-sm text-red-700">{error}</p>
            </div>
          </div>
        )}

        {/* Project Details Section */}
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">Project Details</h3>
          </div>
          <div className="p-4 grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-500">Project Name</p>
              <p className="font-medium text-gray-900">{summary.project.project_name}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">State</p>
              <p className="font-medium text-gray-900">{summary.project.state_name}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Site Address</p>
              <p className="font-medium text-gray-900">{summary.project.site_address}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Local Council</p>
              <p className="font-medium text-gray-900">{summary.project.council_name}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Development Type</p>
              <p className="font-medium text-gray-900">{summary.project.development_type}</p>
            </div>
            <div className="col-span-2">
              <p className="text-sm text-gray-500">Description</p>
              <p className="font-medium text-gray-900">{summary.project.project_description}</p>
            </div>
          </div>
        </div>

        {/* Development Parameters Section */}
        {summary.step2_data && (
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Development Parameters</h3>
            </div>
            <div className="p-4">
              {summary.project.development_type === 'Commercial' && summary.step2_data.commercial_components && (
                <div>
                  <p className="text-sm text-gray-500 mb-2">Commercial Components</p>
                  <div className="space-y-2">
                    {summary.step2_data.commercial_components.map((comp, idx) => (
                      <div key={idx} className="flex justify-between bg-gray-50 px-3 py-2 rounded">
                        <span className="font-medium">{comp.operation_type}</span>
                        <span className="text-gray-600">{Number(comp.gfa).toFixed(0)} m² GFA</span>
                      </div>
                    ))}
                  </div>
                  <p className="mt-2 text-sm text-gray-600">
                    Total GFA: <span className="font-semibold">{Number(summary.step2_data.total_commercial_gfa).toFixed(0)} m²</span>
                  </p>
                </div>
              )}
              
              {summary.project.development_type === 'Residential' && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Total Dwellings</p>
                    <p className="font-medium text-gray-900">{summary.step2_data.dwelling_count}</p>
                  </div>
                  {summary.step2_data.one_bedroom_units > 0 && (
                    <div>
                      <p className="text-sm text-gray-500">1 Bedroom Units</p>
                      <p className="font-medium text-gray-900">{summary.step2_data.one_bedroom_units}</p>
                    </div>
                  )}
                  {summary.step2_data.two_bedroom_units > 0 && (
                    <div>
                      <p className="text-sm text-gray-500">2 Bedroom Units</p>
                      <p className="font-medium text-gray-900">{summary.step2_data.two_bedroom_units}</p>
                    </div>
                  )}
                  {summary.step2_data.three_plus_bedroom_units > 0 && (
                    <div>
                      <p className="text-sm text-gray-500">3+ Bedroom Units</p>
                      <p className="font-medium text-gray-900">{summary.step2_data.three_plus_bedroom_units}</p>
                    </div>
                  )}
                </div>
              )}

              {summary.project.development_type === 'Mixed use' && (
                <div className="space-y-4">
                  <div>
                    <p className="text-sm text-gray-500 mb-2">Commercial Components</p>
                    {summary.step2_data.commercial_components?.map((comp, idx) => (
                      <div key={idx} className="flex justify-between bg-gray-50 px-3 py-2 rounded">
                        <span className="font-medium">{comp.operation_type}</span>
                        <span className="text-gray-600">{Number(comp.gfa).toFixed(0)} m² GFA</span>
                      </div>
                    ))}
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-500">Total Dwellings</p>
                      <p className="font-medium text-gray-900">{summary.step2_data.dwelling_count}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Waste Generation Section - Only for OpWMP (not D&CWMP or CMP) */}
        {!isDCWMP && !isCMP && summary.waste_calculations && (
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Weekly Waste Generation</h3>
            </div>
            <div className="p-4">
              <div className="grid grid-cols-3 gap-4 text-center">
                <div className="bg-gray-100 rounded-lg p-4">
                  <p className="text-sm text-gray-500">General Waste</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {formatNumber(summary.waste_calculations.total?.general_waste || 
                     summary.waste_calculations.residential?.general_waste || 0)}L
                  </p>
                </div>
                <div className="bg-yellow-50 rounded-lg p-4">
                  <p className="text-sm text-gray-500">Recycling</p>
                  <p className="text-2xl font-bold text-yellow-700">
                    {formatNumber(summary.waste_calculations.total?.recycling || 
                     summary.waste_calculations.residential?.recycling || 0)}L
                  </p>
                </div>
                <div className="bg-green-50 rounded-lg p-4">
                  <p className="text-sm text-gray-500">Organics</p>
                  <p className="text-2xl font-bold text-green-700">
                    {formatNumber(summary.waste_calculations.total?.organics || 
                     summary.waste_calculations.residential?.organics || 0)}L
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* D&CWMP Waste Summary Section */}
        {isDCWMP && summary.project.dcwmp_step5 && (
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <div className="bg-amber-50 px-4 py-3 border-b border-amber-200">
              <h3 className="text-lg font-semibold text-gray-900">Waste Estimates Summary</h3>
            </div>
            <div className="p-4 space-y-4">
              {/* Demolition Waste */}
              {summary.project.dcwmp_step5.total_demolition_volume > 0 && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-medium text-gray-900 mb-2">Demolition Waste</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-500">Total Volume</p>
                      <p className="text-xl font-bold text-gray-900">{summary.project.dcwmp_step5.total_demolition_volume?.toFixed(1) || 0} m³</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Diversion Rate</p>
                      <p className="text-xl font-bold text-green-600">{summary.project.dcwmp_step5.demolition_diversion_rate || 0}%</p>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Construction Waste */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-2">Construction Waste</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Total Volume</p>
                    <p className="text-xl font-bold text-gray-900">{summary.project.dcwmp_step5.total_construction_volume?.toFixed(1) || 0} m³</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Diversion Rate</p>
                    <p className="text-xl font-bold text-green-600">{summary.project.dcwmp_step5.construction_diversion_rate || 80}%</p>
                  </div>
                </div>
              </div>
              
              {/* Excavation */}
              {summary.project.dcwmp_step5.excavation_volume > 0 && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-medium text-gray-900 mb-2">Excavation Material</h4>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <p className="text-sm text-gray-500">Total Volume</p>
                      <p className="text-xl font-bold text-gray-900">{summary.project.dcwmp_step5.excavation_volume?.toFixed(1) || 0} m³</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Onsite Reuse</p>
                      <p className="text-xl font-bold text-blue-600">{summary.project.dcwmp_step5.excavation_onsite_reuse?.toFixed(1) || 0} m³</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Exported</p>
                      <p className="text-xl font-bold text-amber-600">{summary.project.dcwmp_step5.excavation_exported?.toFixed(1) || 0} m³</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* CMP Construction Waste Summary Section */}
        {isCMP && summary.project.cmp_data && (
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <div className="bg-indigo-50 px-4 py-3 border-b border-indigo-200">
              <h3 className="text-lg font-semibold text-gray-900">Construction Waste Estimates</h3>
            </div>
            <div className="p-4">
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Total Volume</p>
                    <p className="text-xl font-bold text-gray-900">
                      {summary.project.cmp_data.total_construction_volume?.toFixed(1) || 0} m³
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Diversion Rate</p>
                    <p className="text-xl font-bold text-green-600">
                      {summary.project.cmp_data.construction_diversion_rate || 85}%
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Selected Bin Configuration Section - Only for OpWMP */}
        {!isDCWMP && summary.selected_bin_configuration && (
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Selected Bin Configuration</h3>
            </div>
            <div className="p-4">
              {/* Check if Mixed-Use with separate configs - check for residential_streams in config */}
              {summary.project.development_type === 'Mixed use' && summary.selected_bin_configuration.residential_streams ? (
                <div className="space-y-6">
                  {/* Residential Configuration */}
                  <div>
                    <div className="flex items-center mb-3">
                      <div className="h-6 w-6 rounded-full bg-green-100 flex items-center justify-center mr-2">
                        <svg className="h-4 w-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                        </svg>
                      </div>
                      <span className="font-semibold text-gray-900">Residential Bins</span>
                      <span className="ml-2 text-sm text-gray-500">
                        (Council Collection{summary.step5_data?.selected_residential_configuration ? ` • Option ${summary.step5_data.selected_residential_configuration}` : ''})
                      </span>
                    </div>
                    {summary.selected_bin_configuration.residential_streams && (
                      <div className="grid grid-cols-3 gap-4">
                        <div className="text-center p-3 bg-gray-50 rounded">
                          <p className="text-xs text-gray-500">General Waste</p>
                          <p className="font-bold">{formatBinDisplay(summary.selected_bin_configuration.residential_streams.general_waste)}</p>
                        </div>
                        <div className="text-center p-3 bg-yellow-50 rounded">
                          <p className="text-xs text-gray-500">Recycling</p>
                          <p className="font-bold">{formatBinDisplay(summary.selected_bin_configuration.residential_streams.recycling)}</p>
                        </div>
                        <div className="text-center p-3 bg-green-50 rounded">
                          <p className="text-xs text-gray-500">Organics</p>
                          <p className="font-bold">{formatBinDisplay(summary.selected_bin_configuration.residential_streams.organics)}</p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Commercial Configuration */}
                  <div>
                    <div className="flex items-center mb-3">
                      <div className="h-6 w-6 rounded-full bg-blue-100 flex items-center justify-center mr-2">
                        <svg className="h-4 w-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                        </svg>
                      </div>
                      <span className="font-semibold text-gray-900">Commercial Bins</span>
                      <span className="ml-2 text-sm text-gray-500">
                        (Private Contractor{summary.step5_data?.selected_commercial_configuration ? ` • Option ${summary.step5_data.selected_commercial_configuration}` : ''})
                      </span>
                    </div>
                    {summary.selected_bin_configuration.commercial_streams && (
                      <div className="grid grid-cols-3 gap-4">
                        <div className="text-center p-3 bg-gray-50 rounded">
                          <p className="text-xs text-gray-500">General Waste</p>
                          <p className="font-bold">{formatBinDisplay(summary.selected_bin_configuration.commercial_streams.general_waste)}</p>
                        </div>
                        <div className="text-center p-3 bg-yellow-50 rounded">
                          <p className="text-xs text-gray-500">Recycling</p>
                          <p className="font-bold">{formatBinDisplay(summary.selected_bin_configuration.commercial_streams.recycling)}</p>
                        </div>
                        <div className="text-center p-3 bg-green-50 rounded">
                          <p className="text-xs text-gray-500">Organics</p>
                          <p className="font-bold">{formatBinDisplay(summary.selected_bin_configuration.commercial_streams.organics)}</p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Combined Totals */}
                  <div className="mt-4 pt-4 border-t border-gray-200 flex justify-between text-sm">
                    <span className="text-gray-600">Total Bins (Combined): <span className="font-semibold text-gray-900">{summary.selected_bin_configuration.total_bins}</span></span>
                    <span className="text-gray-600">Total Storage Area: <span className="font-semibold text-gray-900">{formatNumber(summary.selected_bin_configuration.storage_area)} m²</span></span>
                  </div>
                </div>
              ) : (
                /* Non-Mixed-Use: Single configuration */
                <>
                  <p className="text-sm text-gray-600 mb-3">{summary.selected_bin_configuration.description}</p>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="text-center p-3 bg-gray-50 rounded">
                      <p className="text-xs text-gray-500">General Waste</p>
                      <p className="font-bold">{formatBinDisplay(summary.selected_bin_configuration.streams?.general_waste)}</p>
                    </div>
                    <div className="text-center p-3 bg-yellow-50 rounded">
                      <p className="text-xs text-gray-500">Recycling</p>
                      <p className="font-bold">{formatBinDisplay(summary.selected_bin_configuration.streams?.recycling)}</p>
                    </div>
                    <div className="text-center p-3 bg-green-50 rounded">
                      <p className="text-xs text-gray-500">Organics</p>
                      <p className="font-bold">{formatBinDisplay(summary.selected_bin_configuration.streams?.organics)}</p>
                    </div>
                  </div>
                  <div className="mt-4 flex justify-between text-sm">
                    <span className="text-gray-600">Total Bins: <span className="font-semibold text-gray-900">{summary.selected_bin_configuration.total_bins}</span></span>
                    <span className="text-gray-600">Storage Area Required: <span className="font-semibold text-gray-900">{formatNumber(summary.selected_bin_configuration.storage_area)} m²</span></span>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* Management Measures Section - Only for OpWMP */}
        {!isDCWMP && summary.management_measures && summary.management_measures.length > 0 && (
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Waste Management Measures</h3>
            </div>
            <div className="p-4">
              <div className="flex flex-wrap gap-2">
                {summary.management_measures.map((measureId) => (
                  <span 
                    key={measureId}
                    className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-800"
                  >
                    <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    {MEASURE_NAMES[measureId] || measureId}
                  </span>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Document Date Override */}
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">Document Date</h3>
          </div>
          <div className="p-4">
            <p className="text-sm text-gray-600 mb-3">
              Set the date that will appear on your Waste Management Plan document. 
              This will be displayed as &quot;{formatDocumentDate(documentDate)}&quot; on the cover page.
            </p>
            <div className="flex items-center gap-4">
              <input
                type="date"
                value={documentDate}
                onChange={handleDateChange}
                className="px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              />
              <span className="text-sm text-gray-500">
                Document will show: <span className="font-medium">{formatDocumentDate(documentDate)}</span>
              </span>
            </div>
          </div>
        </div>

        {/* Pricing Section */}
        <div className="border-2 border-blue-200 rounded-lg overflow-hidden bg-blue-50">
          <div className="bg-blue-100 px-4 py-3 border-b border-blue-200">
            <h3 className="text-lg font-semibold text-blue-900">Payment Summary</h3>
          </div>
          <div className="p-4">
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-gray-700">Waste Management Plan</span>
                <span className="font-medium">${basePrice.toFixed(2)}</span>
              </div>
              {selectedAddons.length > 0 && selectedAddons.map(key => (
                <div key={key} className="flex justify-between text-gray-700">
                  <span>{addonsConfig[key]?.name || key}</span>
                  <span className="font-medium">${(addonsConfig[key]?.price || 0).toFixed(2)}</span>
                </div>
              ))}
              <div className="flex justify-between text-gray-600">
                <span>GST (10%)</span>
                <span>${gst.toFixed(2)}</span>
              </div>
              <div className="border-t border-blue-200 pt-2 mt-2">
                <div className="flex justify-between text-lg font-bold text-blue-900">
                  <span>Total (AUD)</span>
                  <span>${total.toFixed(2)}</span>
                </div>
              </div>
            </div>
            
            {selectedAddons.length > 0 && (
              <div className="mt-4 p-3 bg-green-50 rounded-md border border-green-200">
                <p className="text-sm text-green-800 flex items-center">
                  <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  {selectedAddons.length} add-on{selectedAddons.length > 1 ? 's' : ''} included in your order
                </p>
              </div>
            )}
            
            <div className="mt-4 p-3 bg-white rounded-md border border-blue-200">
              <p className="text-sm text-gray-600">
                <svg className="inline w-4 h-4 mr-1 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
                {project?.paid 
                  ? "Your plan is paid. You can download or regenerate your PDF anytime."
                  : "After payment, you'll be able to download your professional Waste Management Plan PDF immediately."
                }
              </p>
            </div>
          </div>
        </div>

        {/* Navigation Buttons */}
        <div className="flex justify-between pt-6 border-t border-gray-200">
          <button
            type="button"
            onClick={onBack}
            disabled={processing || redeemingFreePlan}
            className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
          >
            Back
          </button>
          
          {/* Show download button if project is already paid */}
          {project?.paid ? (
            <button
              type="button"
              onClick={handleDownloadPDF}
              disabled={processing}
              className="px-8 py-3 border border-transparent rounded-md shadow-sm text-base font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              {processing ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Downloading...
                </>
              ) : (
                <>
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Download PDF
                </>
              )}
            </button>
          ) : (
            <>
              {/* ==================== FREE PLAN PROMO (START) ==================== */}
              {isFreePlanEligible ? (
                <div className="flex flex-col items-end gap-2">
                  <button
                    type="button"
                    onClick={handleRedeemFreePlan}
                    disabled={redeemingFreePlan}
                    className="px-8 py-3 border-2 border-green-500 rounded-md shadow-sm text-base font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                  >
                    {redeemingFreePlan ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Redeeming...
                      </>
                    ) : (
                      <>
                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
                        </svg>
                        Redeem FREE Plan
                      </>
                    )}
                  </button>
                  <span className="text-xs text-green-600 font-medium">Your first plan is FREE!</span>
                  <button
                    type="button"
                    onClick={handlePayment}
                    disabled={processing || redeemingFreePlan}
                    className="text-xs text-gray-500 hover:text-gray-700 underline"
                  >
                    Or pay ${total.toFixed(2)} AUD instead
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={handlePayment}
                  disabled={processing}
                  className="px-8 py-3 border border-transparent rounded-md shadow-sm text-base font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  {processing ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Processing...
                    </>
                  ) : (
                    <>
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                      </svg>
                      Pay ${total.toFixed(2)} AUD
                    </>
                  )}
                </button>
              )}
              {/* ==================== FREE PLAN PROMO (END) ==================== */}
            </>
          )}
        </div>

        {/* Dev Test Link - Only visible to authorized test user */}
        {currentUserEmail === BYPASS_ALLOWED_EMAIL && (
          <div className="pt-4 text-center border-t border-gray-100">
            <button
              type="button"
              onClick={handleDevTest}
              disabled={processing}
              className="text-xs text-gray-400 hover:text-gray-600 underline disabled:opacity-50"
            >
              {processing ? 'Generating...' : 'Test: Skip payment & download PDF'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Step8ReviewPayment;
